import { CompanySettingListItem } from '@/types/company-settings';
import { Attachment } from '@/library/types/files';

export type SettingOptionPrimitive = string | number | boolean;

export type SettingOptionLike = {
  value?: SettingOptionPrimitive | null;
};

export const getOptionValue = (
  option: SettingOptionPrimitive | SettingOptionLike | null | undefined
): SettingOptionPrimitive | null => {
  if (option == null) return null;

  if (typeof option === 'object') {
    return option.value === undefined ? null : option.value;
  }

  return option;
};

// The api rejects a null value, so an empty setting is stored as '0'. Only for the settings that
// hold free content, since '0' is a meaningful value on the boolean ones, e.g. `form-show-header`
export const EMPTY_SETTING_VALUE = '0';

type CompanySettingValue =
  | string
  | number
  | Attachment
  | number[]
  | Attachment[]
  | null
  | undefined;

export const isSettingEmpty = (value: CompanySettingValue) =>
  Array.isArray(value) ? value.length === 0 : !value || value === EMPTY_SETTING_VALUE;

export const getSettingFileId = (value: unknown): number | null => {
  if (value == null || value === '' || value === EMPTY_SETTING_VALUE) return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (Array.isArray(value)) {
    if (!value.length) return null;
    return getSettingFileId(value[0]);
  }

  if (typeof value === 'object' && 'id' in value) {
    return getSettingFileId((value as { id: unknown }).id);
  }

  return null;
};

export const compactCrewExternalIdKeys = (
  items: Array<{ key?: string | null; label?: string | null }> | null | undefined
): CompanySettingListItem[] =>
  (items || []).flatMap(item =>
    item.key && item.label ? [{ key: item.key, label: item.label }] : []
  );
